/**
 * syncQueueStore.ts
 *
 * Raw read/write for the `syncQueue` object store.
 * This is the operation log — the watcher reads exclusively from here.
 *
 * Each op lives in one of three states:
 *   isSync: 0 — pending sync
 *   isSync: 1 — successfully synced to MongoDB (eligible for pruning)
 *   isSync: 2 — dead-letter, permanently failed after MAX_ATTEMPTS (never auto-pruned)
 *
 * RULE: Never auto-delete ops in state 0 or 2. State 2 is the tombstone that
 * keeps a record of the failure (payload + syncError) for telemetry / manual recovery.
 */

import { appDB } from "../db/client";
import type {
  SyncQueueRecord,
  SyncOperationType,
  ReminderUpsertPayload,
  ReminderCancelPayload,
} from "../db/schema";
import type {
  BlockChangesPayload,
} from "@/types/block";
import type { SidebarReorderPayload, WorkAreaOperationPayload } from "../db/schema";

// ─── Add operations ───────────────────────────────────────────────

/**
 * Add a BLOCK_CHANGES operation to the queue.
 * Called inside blockMutations — always within a transaction.
 */
export async function enqueueBlockChanges(
  payload: BlockChangesPayload,
  noteId: string
): Promise<string> {
  const id = crypto.randomUUID();
  await appDB.syncQueue.add({
    id,
    operationType: "BLOCK_CHANGES",
    payload,
    createdAt: Date.now(),
    isSync: 0 as 0,
    attempts: 0,
    lastAttemptAt: null,
    syncError: null,
    noteId,
    workspaceId: payload.workspaceId,
  });
  return id;
}

/**
 * Add a SIDEBAR_REORDER operation to the queue.
 * Called inside sidebarMutations.
 */
export async function enqueueSidebarReorder(
  payload: SidebarReorderPayload
): Promise<string> {
  const id = crypto.randomUUID();
  await appDB.syncQueue.add({
    id,
    operationType: "SIDEBAR_REORDER",
    payload,
    createdAt: Date.now(),
    isSync: 0 as 0,
    attempts: 0,
    lastAttemptAt: null,
    syncError: null,
    noteId: null,
    workspaceId: payload.workspaceId,
  });
  return id;
}

/**
 * Add a WORKAREA_CREATE / UPDATE / DELETE operation to the queue.
 * Called inside workAreaMutations.
 */
export async function enqueueWorkAreaOperation(
  operationType: "WORKAREA_CREATE" | "WORKAREA_UPDATE" | "WORKAREA_DELETE",
  payload: WorkAreaOperationPayload
): Promise<string> {
  const id = crypto.randomUUID();
  await appDB.syncQueue.add({
    id,
    operationType,
    payload,
    createdAt: Date.now(),
    isSync: 0 as 0,
    attempts: 0,
    lastAttemptAt: null,
    syncError: null,
    noteId: null,
    workspaceId: payload.workspaceId,
  });
  return id;
}

// ─── Reads (used by watcher) ──────────────────────────────────────

/**
 * Get all unsynced operations ordered by createdAt ascending (oldest first).
 * The watcher calls this to know what to process.
 */
export async function getUnsyncedOps(workspaceId: string): Promise<SyncQueueRecord[]> {
  return appDB.syncQueue
    .where("workspaceId")
    .equals(workspaceId)
    .filter((op) => op.isSync === 0)
    .sortBy("createdAt");
}

/**
 * Get all unsynced ops for a specific note (for per-note serialization).
 */
export async function getUnsyncedOpsForNote(noteId: string): Promise<SyncQueueRecord[]> {
  return appDB.syncQueue
    .where("noteId")
    .equals(noteId)
    .and((op) => op.isSync === 0)
    .sortBy("createdAt");
}

/**
 * Derive the set of block ids with PENDING unsynced work from the syncQueue
 * itself (Step 3 of BLOCK_STORE_REDESIGN.md).
 *
 * This replaces every read of the per-row `blocks.isSync` flag. The queue is
 * the sync engine's actual source of truth (getUnsyncedOps), so "an unsynced
 * op references this block" is strictly more authoritative than a row flag —
 * and it lets sync-acks skip writing the blocks table entirely (the write
 * that used to re-fire every per-key liveQuery observer after every save).
 * When SyncWatcher marks ops synced, blocks drop out of this set
 * automatically; nothing needs to be written back to `blocks`.
 *
 * The extraction is a deliberate PROTECTIVE SUPERSET (noteId, parentId, and
 * created/updated/deleted block ids): callers use it to decide what NOT to
 * evict or overwrite, so over-inclusion merely retains a few extra blocks.
 *
 * DEAD-LETTERED ops (isSync: 2 — permanently failed after MAX_ATTEMPTS) are
 * included too: their local data never reached the server, so the referenced
 * blocks must keep their eviction/overwrite protection until the op is
 * manually recovered or discarded. This matches the old per-row flag's
 * behavior, which stayed 0 forever for blocks whose sync never succeeded.
 */
export async function getPendingBlockIds(): Promise<Set<string>> {
  const ids = new Set<string>();
  try {
    const ops = await appDB.syncQueue.where("isSync").anyOf(0, 2).toArray();
    for (const op of ops) {
      if (op.noteId) ids.add(op.noteId);
      const p = op.payload as Record<string, any> | undefined;
      if (!p) continue;
      if (typeof p.parentId === "string") ids.add(p.parentId);
      if (typeof p.blockId === "string") ids.add(p.blockId);
      if (Array.isArray(p.blocks)) for (const b of p.blocks) if (b?._id) ids.add(b._id);
      if (Array.isArray(p.creates)) for (const c of p.creates) if (c?.blockId) ids.add(c.blockId);
      if (Array.isArray(p.updates)) for (const u of p.updates) if (u?.blockId) ids.add(u.blockId);
      if (Array.isArray(p.deletes)) for (const d of p.deletes) if (d?.blockId) ids.add(d.blockId);
    }
  } catch {
    // IDB unavailable — empty set. That matches the no-IDB direct-API mode,
    // where nothing is ever locally pending.
  }
  return ids;
}

/**
 * Count all unsynced operations across the entire queue.
 * Used to update syncMeta.pendingCount.
 */
export async function countPendingOps(): Promise<number> {
  return appDB.syncQueue.filter((op) => op.isSync === 0).count();
}

/**
 * True while a BLOCK_CREATE carrying this datasource has never reached the
 * server — pending (0) or dead-lettered (2). Either way a 404 is expected and
 * means "never created", not "deleted".
 * A record's isSync: 0 can't be used — unsynced edits look identical.
 */
export async function hasPendingDatasourceCreation(dataSourceId: string): Promise<boolean> {
  try {
    const id = String(dataSourceId);
    const ops = await appDB.syncQueue.where("isSync").anyOf([0, 2]).toArray();
    return ops.some(
      (op) =>
        op.operationType === "BLOCK_CREATE" &&
        String((op.payload as any)?.dataSourceDetail?._id ?? "") === id,
    );
  } catch {
    return false;
  }
}

// ─── Status updates ───────────────────────────────────────────────

/**
 * Mark a single operation as successfully synced.
 */
export async function markOpSynced(opId: string): Promise<void> {
  await appDB.syncQueue.update(opId, {
    isSync: 1 as 1,
    syncError: null,
  });
}

/**
 * Mark multiple operations as synced in one call.
 */
export async function markOpsSynced(opIds: string[]): Promise<void> {
  await appDB.transaction("rw", appDB.syncQueue, async () => {
    await Promise.all(
      opIds.map((id) =>
        appDB.syncQueue.update(id, { isSync: 1 as 1, syncError: null })
      )
    );
  });
}

/**
 * Record a failed sync attempt on an operation.
 * Increments attempts and records the error message.
 */
export async function recordOpFailure(opId: string, error: string): Promise<void> {
  const existing = await appDB.syncQueue.get(opId);
  if (!existing) return;
  await appDB.syncQueue.update(opId, {
    attempts: existing.attempts + 1,
    lastAttemptAt: Date.now(),
    syncError: error,
  });
}

/**
 * Move ops into the dead-letter state (isSync: 2). Called by the watcher when
 * attempts >= MAX_ATTEMPTS. The op stays in the queue with its payload + syncError
 * intact so it can be surfaced or recovered, but it is excluded from getUnsyncedOps()
 * and will no longer be retried.
 */
export async function markOpsDeadLetter(opIds: string[]): Promise<void> {
  if (opIds.length === 0) return;
  await appDB.transaction("rw", appDB.syncQueue, async () => {
    await Promise.all(
      opIds.map((id) => appDB.syncQueue.update(id, { isSync: 2 as 2 }))
    );
  });
}

/**
 * Get all permanently failed (dead-letter) operations.
 * These are surfaced as UI errors / available for manual recovery.
 */
export async function getPermanentlyFailedOps(): Promise<SyncQueueRecord[]> {
  return appDB.syncQueue.where("isSync").equals(2).toArray();
}

/**
 * Prune successfully synced operations older than retentionDays.
 * Safe to call periodically — only deletes isSync: 1 records.
 */
export async function pruneSyncedOps(retentionDays = 7): Promise<void> {
  const cutoff = Date.now() - retentionDays * 24 * 60 * 60 * 1000;
  // Use isSync index to narrow to synced ops, then filter by age
  const keysToDelete = await appDB.syncQueue
    .where("isSync").equals(1)
    .filter((op) => op.createdAt < cutoff)
    .primaryKeys();
  await appDB.syncQueue.bulkDelete(keysToDelete);
}

/** Queued rather than POSTed, so a reminder set offline survives to reconnect. */
export async function enqueueReminderUpsert(
  payload: ReminderUpsertPayload
): Promise<string> {
  const id = crypto.randomUUID();
  await appDB.syncQueue.add({
    id,
    operationType: "REMINDER_UPSERT",
    payload,
    createdAt: Date.now(),
    isSync: 0 as const,
    attempts: 0,
    lastAttemptAt: null,
    syncError: null,
    noteId: payload.noteId,
    workspaceId: payload.workspaceId,
  });
  return id;
}

export async function enqueueReminderCancel(
  payload: ReminderCancelPayload
): Promise<string> {
  const id = crypto.randomUUID();
  await appDB.syncQueue.add({
    id,
    operationType: "REMINDER_CANCEL",
    payload,
    createdAt: Date.now(),
    isSync: 0 as const,
    attempts: 0,
    lastAttemptAt: null,
    syncError: null,
    noteId: payload.noteId,
    workspaceId: payload.workspaceId,
  });
  return id;
}
